/**
 * Widget catalog (screenshots/73): 7 shared, 4 resident, 5 office, 5 advocacy and 3 data widgets, as views over the one
 * widget registry (widgets/registry.ts). Each is gated by can(group, role, feature, features) and lists the sizes it
 * supports; `features` is the server's map for the viewer (staff overrides), omitted = the viewer's / group defaults.
 * Pure; tests/overview.test.ts covers the gates and size rules.
 */
import { can, type FeatureMap, type OrgRole, type UserGroup } from "../nav/navConfig";
import type { WidgetSize } from "./charts/chartTypes";
import type { WidgetType } from "./types";
import { WIDGETS, libraryFor, type Audience, type WidgetDef } from "./widgets/registry";

export type CatalogSection = Audience;
export type CatalogEntry = WidgetDef;

/** The 24 library widgets, in catalog order. */
export const CATALOG: CatalogEntry[] = Object.values(WIDGETS).filter((x) => x.inLibrary !== false);
export const entryOf = (t: WidgetType): CatalogEntry => WIDGETS[t];

/** What this account can add (and render). */
export const allowed = (entry: CatalogEntry, group: UserGroup, role: OrgRole | null, features?: FeatureMap | null) =>
  (entry.audience === "shared" || entry.audience === "data" || entry.audience === group) && (!entry.feature || can(group, role, entry.feature, features));

export const catalogFor = (group: UserGroup, role: OrgRole | null, features?: FeatureMap | null) => libraryFor(group, role, features);
/** Widgets a group's default layout shows outside its own catalog: the office default has a Toss-ups tile (70). */
const DEFAULT_EXTRAS: Partial<Record<UserGroup, WidgetType[]>> = { office: ["tossUps"] };
export const canRender = (t: WidgetType, group: UserGroup, role: OrgRole | null, features?: FeatureMap | null) =>
  !!WIDGETS[t] && (allowed(entryOf(t), group, role, features) || !!DEFAULT_EXTRAS[group]?.includes(t));

/** Library tabs (68): All · Your team (your group's own widgets) · Shared · Charts & tables. */
export type LibraryTab = "all" | "team" | "shared" | "data";
export const libraryTabOf = (x: CatalogEntry): Exclude<LibraryTab, "all"> =>
  x.audience === "shared" ? "shared" : x.audience === "data" ? "data" : "team";

/** "S · M · L" (73/68). */
export const sizesText = (x: CatalogEntry) => x.sizes.join(" · ");

/** Cell rules for a size (S: w ≤ 3 or h ≤ 2; M: w ≤ 6 and h ≤ 3; L: bigger), so resizes stay inside a widget's sizes. */
export function sizeAllowed(entry: Pick<CatalogEntry, "sizes">, w: number, h: number): boolean {
  const s: WidgetSize = w <= 3 || h <= 2 ? "S" : w <= 6 && h <= 3 ? "M" : "L";
  return entry.sizes.includes(s);
}
